import React, { useState } from 'react';
import {
  Badge,
  Button,
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  LinearProgress,
  useTheme,
  Paper,
  Tooltip,
  alpha,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useUploadStore } from '../../stores/uploadStore';

export const DocumentManagerActiveUploads: React.FC = () => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const { activeUploads, clearCompletedUploads } = useUploadStore();

  // Count total active uploads
  const totalActiveUploads = activeUploads.length;

  // Count completed uploads
  const completedUploads = activeUploads.filter(job =>
    Object.values(job.status).every(status => status.isComplete)
  ).length;

  // Count active uploads (uploads in progress)
  const activeUploadCount = totalActiveUploads - completedUploads;

  // If no active uploads, don't render anything
  if (totalActiveUploads === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          cursor: 'pointer',
          bgcolor: expanded ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={activeUploadCount} color="primary">
            <CloudUploadIcon color="primary" />
          </Badge>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {activeUploadCount > 0
              ? `${activeUploadCount} Uploads in Progress`
              : 'All Uploads Complete'}
          </Typography>
        </Box>
        <IconButton size="small">
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {completedUploads > 0 && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="text" size="small" onClick={clearCompletedUploads}>
                Clear Completed Uploads
              </Button>
            </Box>
          )}

          <List
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              '& > li:not(:last-child)': {
                mb: 1.5,
              },
            }}
          >
            {activeUploads.map(job => {
              const uploadTime = new Date(job.createdAt).toLocaleTimeString();
              const fileCount = Object.keys(job.status).length;
              const completedCount = Object.values(job.status).filter(s => s.isComplete).length;
              const isComplete = completedCount === fileCount;
              const hasErrors = Object.values(job.status).some(s => s.error);

              return (
                <ListItem
                  key={job.id}
                  disablePadding
                  sx={{
                    display: 'block',
                    bgcolor: isComplete
                      ? hasErrors
                        ? alpha(theme.palette.warning.light, 0.2)
                        : alpha(theme.palette.success.light, 0.2)
                      : alpha(theme.palette.background.default, 0.5),
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'medium',
                      color: isComplete
                        ? hasErrors
                          ? theme.palette.warning.main
                          : theme.palette.success.main
                        : theme.palette.text.primary,
                    }}
                  >
                    Upload at {uploadTime} - {completedCount}/{fileCount} files
                  </Typography>

                  <LinearProgress
                    variant="determinate"
                    value={(completedCount / fileCount) * 100}
                    sx={{
                      my: 1,
                      height: 6,
                      borderRadius: 1,
                      backgroundColor: hasErrors ? theme.palette.warning.light : undefined,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: hasErrors ? theme.palette.warning.main : undefined,
                        borderRadius: 1,
                      },
                    }}
                  />

                  <List dense disablePadding>
                    {Object.entries(job.status).map(([filename, status]) => (
                      <ListItem
                        key={filename}
                        disablePadding
                        sx={{
                          py: 0.5,
                          display: 'block',
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            color: status.error
                              ? theme.palette.error.main
                              : status.isComplete
                                ? theme.palette.success.main
                                : theme.palette.text.primary,
                          }}
                        >
                          {filename}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: status.error
                              ? theme.palette.error.main
                              : theme.palette.text.secondary,
                          }}
                        >
                          {status.error ? `Error: ${status.error}` : status.progress}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Collapse>
    </Paper>
  );
};
